import GaugeData, { WidgetGaugeComparison } from 'domain/entity/widgets/GaugeData';
import React, { memo, ReactElement } from 'react';
import Branding from 'domain/entity/branding';
import { Gauge } from '@dexma/ui-components';
import useGaugeWidget from 'adapter/in/common/hooks/widget/useGaugeWidget';
import { getColor, getPeriodFrom } from 'adapter/in/common/utils/widgetDataUtils';
import DataAvailability from 'adapter/in/common/components/widgets/DataAvailability';
import { checkIfHasComparisonData, checkIfHasMainData } from 'adapter/in/common/components/widgets/common/checkIfHasData';
import { Options } from 'highcharts';
import { checkMemoChanges } from 'adapter/in/common/components/widgets/common/checkRedraw';

type WidgetGaugeProps = {
    height: number;
    width: number;
    isOnEdit: boolean;
    dateFormat: string;
    data: GaugeData;
    decimalSeparator: string;
    thousandSeparator: string;
    branding: Branding;
    isLoading: boolean;
    showDataAvailability: boolean;
};

const WidgetGauge = (props: WidgetGaugeProps): ReactElement => {
    const {
        data,
        dateFormat,
        branding,
        height,
        thousandSeparator,
        decimalSeparator,
        isLoading,
        isOnEdit,
        showDataAvailability,
        width,
    } = props;
    const from = isLoading ? 'last seven days' : getPeriodFrom(data.description.period, dateFormat);
    const { parseGaugeIndicator, parseGaugeRange, getComparisonText, parseComparisonIndicator } = useGaugeWidget(
        branding,
        dateFormat,
        decimalSeparator,
        thousandSeparator
    );
    const chartOptions: Options = {
        chart: {
            borderRadius: 8,
            height: height - 4,
            width: width - 4,
            zooming: {
                type: isOnEdit || isLoading ? undefined : 'x',
            },
        },
        subtitle: {
            text: isLoading ? 'Sample subtitle' : from.charAt(0).toUpperCase() + from.slice(1),
        },
        plotOptions: {
            series: {
                animation: false,
            },
        },
    };
    const hasMainData = isLoading ? false : checkIfHasMainData(data.dataAvailability);

    const getWidgetGaugeComparison = (gaugeData: GaugeData): WidgetGaugeComparison => {
        const hasComparisonData = isLoading ? false : checkIfHasComparisonData(gaugeData.dataAvailability);

        if (gaugeData.comparison && gaugeData.comparison.enabled && !hasComparisonData) {
            return new WidgetGaugeComparison(
                gaugeData.comparison.color,
                gaugeData.comparison.enabled,
                {
                    type: gaugeData.comparison.period.type,
                    from: gaugeData.comparison.period.from,
                    to: gaugeData.comparison.period.to,
                    text: getComparisonText(gaugeData.comparison),
                },
                gaugeData.comparison.showAsPercentage
            );
        }
        if (gaugeData.comparison && gaugeData.comparison.enabled && hasComparisonData) {
            return new WidgetGaugeComparison(
                gaugeData.comparison.color,
                gaugeData.comparison.enabled,
                {
                    type: gaugeData.comparison.period.type,
                    from: gaugeData.comparison.period.from,
                    to: gaugeData.comparison.period.to,
                    text: getComparisonText(gaugeData.comparison),
                },
                gaugeData.comparison.showAsPercentage,
                gaugeData.comparison.value
            );
        }

        return { ...gaugeData.comparison, enabled: false };
    };
    const compare = getWidgetGaugeComparison(data);

    let checkpoints =
        data.checkpoints && data.checkpoints.length > 0
            ? data.checkpoints
                  .filter((checkpoint) => checkpoint.value <= data.max && checkpoint.value >= data.min)
                  .map((checkpoint) => parseGaugeIndicator(checkpoint, checkpoint.name!, data.units))
            : [];

    if (compare.enabled && hasMainData && Number(data.comparison.value) <= data.max && Number(data.comparison.value) >= data.min) {
        checkpoints = [...checkpoints, parseComparisonIndicator(data.comparison, data.description.title, data.units)];
    }
    const comparison = data.comparison && data.comparison.enabled && { ...compare, color: getColor(branding)(data.comparison.color) };
    const ranges = data.ranges && data.ranges.length > 0 ? data.ranges.map((range) => parseGaugeRange(range, data.min, data.max)) : [];
    const indicator = data.indicator && parseGaugeIndicator(data.indicator, data.description.title, data.units, data.description.period);

    return (
        <>
            {!isLoading && (
                <Gauge
                    data-testid='gauge-chart'
                    checkpoints={checkpoints}
                    comparison={
                        comparison
                            ? { ...comparison, period: { ...comparison.period, type: comparison.period.type.toString() } }
                            : undefined
                    }
                    decimalPoint={decimalSeparator}
                    indicator={indicator}
                    max={data.max}
                    min={data.min}
                    hasData={hasMainData}
                    title={data.description.title}
                    options={chartOptions}
                    ranges={ranges}
                    showAsPercentage={data.showAsPercentage}
                    thousandsSep={thousandSeparator}
                    type='DIAL'
                    units={` ${data.units}`}
                />
            )}
            {!isLoading && !isOnEdit && showDataAvailability && <DataAvailability dataAvailability={data.dataAvailability} />}
        </>
    );
};

export default memo(WidgetGauge, checkMemoChanges);
